import React, { useMemo, useState, useEffect } from 'react';
import { Button, Tree, Typography, Dropdown, Tag, Input } from 'antd';
import { PlusOutlined, ArrowLeftOutlined, MoreOutlined } from '@ant-design/icons';
import useWorkflowTree from '../hooks/useWorkflowTree';
import { getNodeMeta } from '../constants/nodeTypes';

const { Text } = Typography;

function toTreeData(items) {
  return (items || []).map((item) => {
    if (item.branchLabel && item.nodes) {
      return {
        key: item.branchKey,
        title: item.branchLabel,
        selectable: false,
        children: toTreeData(item.nodes),
      };
    }
    const meta = item.meta || getNodeMeta(item.node?.type);
    const Icon = meta.icon;
    return {
      key: item.key,
      title: (
        <span>
          {Icon && <Icon style={{ marginRight: 6, color: meta.color }} />}
          {meta.listLabel?.(item.node?.config) || meta.label}
        </span>
      ),
      children: item.children?.length ? toTreeData(item.children) : undefined,
    };
  });
}

const FlowStructureTree = ({
  definition,
  selectedNodeId,
  onSelectNode,
  onAddNode,
  style,
  flowName,
  status,
  onBack,
  onRename,
  onDuplicate,
  onDelete,
  onExportJson,
  onImportJson,
}) => {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(flowName);

  useEffect(() => {
    setName(flowName);
  }, [flowName]);

  const menuItems = [
    { key: 'rename', label: 'Rename flow' },
    { key: 'duplicate', label: 'Duplicate flow' },
    { key: 'export', label: 'View JSON' },
    { key: 'import', label: 'Import JSON' },
    { type: 'divider' },
    { key: 'delete', label: 'Delete flow', danger: true },
  ];

  const workflowTree = useWorkflowTree(definition);
  const treeData = useMemo(() => toTreeData(workflowTree), [workflowTree]);

  return (
    <div className="flow-builder-sider" style={style}>
      {/* Sidebar Header with Back arrow, Flow Title, Status and actions dropdown */}
      <div
        style={{
          padding: '12px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between' }}>
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ padding: 0, width: 24, height: 24 }}
          />
          <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
            {editingName ? (
              <Input
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  setEditingName(false);
                  if (name !== flowName) onRename?.(name);
                }}
                onPressEnter={() => {
                  setEditingName(false);
                  if (name !== flowName) onRename?.(name);
                }}
                style={{ width: '100%' }}
                autoFocus
              />
            ) : (
              <Text
                strong
                style={{ cursor: 'pointer', fontSize: 14, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={() => setEditingName(true)}
              >
                {flowName || 'Untitled flow'}
              </Text>
            )}
          </div>
          <Dropdown
            menu={{
              items: menuItems,
              onClick: ({ key }) => {
                if (key === 'rename') setEditingName(true);
                if (key === 'duplicate') onDuplicate?.();
                if (key === 'export') onExportJson?.();
                if (key === 'import') onImportJson?.();
                if (key === 'delete') onDelete?.();
              },
            }}
            trigger={['click']}
          >
            <Button type="text" size="small" icon={<MoreOutlined />} style={{ padding: 0, width: 24, height: 24 }} />
          </Dropdown>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tag color={status === 'published' ? 'green' : 'default'} style={{ margin: 0 }}>
            {status === 'published' ? 'Published' : 'Draft'}
          </Tag>
          <Button type="text" size="small" icon={<PlusOutlined />} onClick={onAddNode} style={{ height: 22, padding: '0 4px', fontSize: 11 }}>
            Add
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 8px' }}>
        {treeData.length === 0 ? (
          <Text type="secondary" style={{ padding: 8, display: 'block' }}>
            No nodes yet
          </Text>
        ) : (
          <Tree
            showLine
            defaultExpandAll
            selectedKeys={selectedNodeId ? [selectedNodeId] : []}
            onSelect={(keys) => keys[0] && onSelectNode(keys[0])}
            treeData={treeData}
          />
        )}
      </div>
    </div>
  );
};

export default FlowStructureTree;
